import { useMemo, useState } from 'react';
import { ActionList } from '../../components/ui';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SortIcon from '../../components/ui/Sorting';
import SortSummary from '../../components/ui/SortSummary';
import { formatDateTime } from '../../helpers/formatDateTime';
import type { DistributionGroupSummary } from '../../services/distribution/distribution.types';

const COLUMNS = [
    { key: 'profileName', label: 'Profile' },
    { key: 'recipientCount', label: 'Recipients' },
    { key: 'activeRecipientCount', label: 'Active' },
    { key: 'hasEmailRecipients', label: 'Email' },
    { key: 'hasSmsRecipients', label: 'SMS' },
    { key: 'createdAt', label: 'Created' },
] as const;
type SortColumn = typeof COLUMNS[number]['key'];

interface Props {
    groups: DistributionGroupSummary[];
    loading: boolean;
    editing: boolean;
    onEdit: (group: DistributionGroupSummary) => void;
    onDelete: (group: DistributionGroupSummary) => void;
}

export function DistributionGroupList({ groups, loading, editing, onEdit, onDelete }: Props) {
    const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [hasAppliedSort, setHasAppliedSort] = useState(false);
    const activeColumn = COLUMNS.find(column => column.key === sortColumn);

    const handleSort = (column: SortColumn) => {
        setHasAppliedSort(true);
        setSortColumn(column);
        setSortOrder(previous => sortColumn === column && previous === 'asc' ? 'desc' : 'asc');
    };

    const resetSort = () => {
        setSortColumn('createdAt');
        setSortOrder('desc');
        setHasAppliedSort(false);
    };

    const sortedGroups = useMemo(() => [...groups].sort((left, right) => {
        let comparison: number;
        if (sortColumn === 'profileName') {
            const first = (left.profileName ?? '').toLowerCase();
            const second = (right.profileName ?? '').toLowerCase();
            comparison = first < second ? -1 : first > second ? 1 : 0;
        } else if (sortColumn === 'createdAt') {
            comparison = (left.createdAt ? new Date(left.createdAt).getTime() : 0) -
                (right.createdAt ? new Date(right.createdAt).getTime() : 0);
        } else {
            comparison = Number(left[sortColumn] ?? 0) - Number(right[sortColumn] ?? 0);
        }
        return comparison * (sortOrder === 'asc' ? 1 : -1);
    }), [groups, sortColumn, sortOrder]);

    return (
        <>
            <div className="alarm-content--heading" aria-hidden={editing}>
                <div>
                    <h3 className="heading heading__title--building">Group List</h3>
                    {hasAppliedSort && activeColumn && (
                        <SortSummary columnLabel={activeColumn.label} sortOrder={sortOrder}
                            onReset={resetSort} resetAriaLabel="Reset sorting to newest first" />
                    )}
                </div>
            </div>
            {loading && <LoadingSpinner message="Loading distribution groups…" />}
            {!loading && (
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                {COLUMNS.map(column => (
                                    <th key={column.key} onClick={() => handleSort(column.key)}
                                        aria-sort={sortColumn === column.key ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        className="th--sortable">
                                        {column.label} <SortIcon sortOrder={sortColumn === column.key ? sortOrder : null} />
                                    </th>
                                ))}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedGroups.map(group => (
                                <tr key={group.id}>
                                    <td>{group.profileName}</td>
                                    <td>{group.recipientCount}</td>
                                    <td>{group.activeRecipientCount}</td>
                                    <td>{group.hasEmailRecipients ? 'Yes' : 'No'}</td>
                                    <td>{group.hasSmsRecipients ? 'Yes' : 'No'}</td>
                                    <td>{formatDateTime(group.createdAt)}</td>
                                    <td><ActionList onEdit={() => onEdit(group)} onDelete={() => onDelete(group)} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
